import { useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import Layout from "../common/Layout";
import { usePageMeta } from "../common/usePageMeta";
import { BadgeCheck, ScanEye, Route, Sparkles, CircuitBoard, Sprout, Bug, PackageCheck, Leaf, Droplet, Timer, Activity } from "lucide-react";
import { track } from "../features/analytics/tracker";
import EhoBadge from "../common/EhoBadge";

const item = { hidden:{opacity:0, y:12}, show:{opacity:1, y:0, transition:{duration:.25}} };

export default function AboutPage() {
  usePageMeta({ title: 'About the Grower | TearDrop Hydro', description: 'The story behind TearDrop Hydroponics: a one-person indoor farm in Hockley growing pesticide-free herbs and salads for Essex.', path: '/about' });
  const seen = useRef(new Set());

  // helper to track section visibility once
  const onVisible = (key, extra = {}) => {
    if (seen.current.has(key)) return;
    seen.current.add(key);
    track(key, extra);
  };

  // page view
  useEffect(() => {
    track("about_view");
  }, []);

  return (
    <Layout>
      {/* HERO */}
      <section style={{ position: "relative", overflow: "hidden", minHeight: "clamp(380px, 55svh, 600px)", display: "flex", alignItems: "center" }}>
        <img
          src="/images/products/mix/IMG_4392.JPEG"
          alt=""
          style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", objectPosition: "center 35%" }}
        />
        <div style={{ position: "absolute", inset: 0, background: "linear-gradient(135deg, rgba(10,28,20,.82) 0%, rgba(10,28,20,.52) 60%, transparent 100%)" }} />
        <div className="container" style={{ position: "relative", zIndex: 1, padding: "var(--s-10) var(--s-5)" }}>
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <p className="eyebrow" style={{ color: "var(--accent)", marginBottom: "var(--s-3)" }}>About TearDrop</p>
            <h1 className="h1" style={{ color: "#fff", maxWidth: 580, marginTop: 0, marginBottom: "var(--s-4)", fontSize: "clamp(28px,5vw,44px)", lineHeight: 1.1 }}>
              A small farm in Hockley, doing this the right way.
            </h1>
            <p style={{ color: "rgba(255,255,255,.8)", fontSize: "clamp(15px,1.8vw,18px)", maxWidth: 500, lineHeight: 1.6 }}>
              I’m Michael, and I run a vertical hydroponic farm out of Hockley. No pesticides, minimal
              water use, and a delivery route that rarely exceeds three miles.
            </p>
          </motion.div>
        </div>
      </section>

      <div className="container section about">

        {/* Founder story */}
        <motion.section
          className="about-split"
          initial="hidden"
          whileInView="show"
          viewport={{ once:true, amount:.3 }}
          variants={{hidden:{}, show:{transition:{staggerChildren:.06}}}}
          onViewportEnter={() => onVisible("about_section_visible", { section: "founder_story" })}
        >
          <motion.div className="about-text" variants={item}>
            <p className="body">
              Shop-bought greens spend days in transit before they reach you, losing flavour along the way.
              So I built a vertical hydroponic farm myself, right down to the grow lights and sensors, to grow
              greens that are cut when they're ready, not when a delivery schedule demands it.
            </p>
          </motion.div>
          <motion.ul className="about-facts" variants={item} role="list">
              <li>
                <span className="about-fact-icon" aria-hidden="true"><Leaf size={16} /></span>
                <strong>0</strong>
                <span>Chemical pesticides</span>
              </li>
              <li>
                <span className="about-fact-icon" aria-hidden="true"><Droplet size={16} /></span>
                <strong>90%</strong>
                <span>Less water than soil</span>
              </li>
              <li>
                <span className="about-fact-icon" aria-hidden="true"><Timer size={16} /></span>
                <strong>1–3 hrs</strong>
                <span>Farm to door</span>
              </li>
              <li>
                <span className="about-fact-icon" aria-hidden="true"><Activity size={16} /></span>
                <strong>24/7</strong>
                <span>Climate monitoring</span>
              </li>
          </motion.ul>
        </motion.section>

        {/* Built in-house */}
        <motion.section
          className="about-how"
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: .3 }}
          variants={{ hidden: {}, show: { transition: { staggerChildren: .06 } } }}
          onViewportEnter={() => onVisible("about_section_visible", { section: "built_in_house" })}
        >
          <motion.h2 className="h3" variants={item} style={{ padding: "0 20px" }}>Engineered for the crop, not off the shelf</motion.h2>

          <div className="how-grid how-band">
            <motion.div className="how-card" variants={item}>
              <div className="step-icon" aria-hidden="true"><CircuitBoard size={22} /></div>
              <h3 className="h4">Custom-built electronics</h3>
              <p className="muted">
                Grow lights and climate sensors, designed and built by me, in-house, rather than bought off the shelf.
              </p>
            </motion.div>

            <motion.div className="how-card" variants={item}>
              <div className="step-icon" aria-hidden="true"><Sprout size={22} /></div>
              <h3 className="h4">Tuned tray by tray</h3>
              <p className="muted">
                Light, feed, and climate are set per crop, not one blanket recipe for everything on the rack.
              </p>
            </motion.div>

            <motion.div className="how-card" variants={item}>
              <div className="step-icon" aria-hidden="true"><Bug size={22} /></div>
              <h3 className="h4">Pests, handled naturally</h3>
              <p className="muted">
                Beneficial insects like ladybugs keep pests down. No harsh chemicals, ever.
              </p>
            </motion.div>

            <motion.div className="how-card" variants={item}>
              <div className="step-icon" aria-hidden="true"><PackageCheck size={22} /></div>
              <h3 className="h4">A shorter, safer chain</h3>
              <p className="muted">
                No distributors or holding warehouses in between: fewer hands, less time, less risk.
              </p>
            </motion.div>
          </div>
        </motion.section>

        {/* Values / Manifesto */}
        <motion.section
          className="about-values fancy-flow"
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: .3 }}
          variants={{ hidden: {}, show: { transition: { staggerChildren: .06 } } }}
          onViewportEnter={() => onVisible("about_section_visible", { section: "values_manifesto" })}
        >
          <motion.h2 className="h3" variants={item}>What TearDrop stands for</motion.h2>

          <ol className="flow" role="list">
            <motion.li
              className="step"
              variants={item}
              onViewportEnter={() => onVisible("about_value_visible", { value: "consistency" })}
            >
              <span className="step-dot"><span>1</span></span>
              <div className="step-icon" aria-hidden="true"><BadgeCheck size={22} /></div>
              <h3 className="h4">Consistency</h3>
              <p className="muted">
                Every tray is logged, monitored, and tuned for reliable results. The flavour shows it.
              </p>
            </motion.li>

            <motion.li
              className="step"
              variants={item}
              onViewportEnter={() => onVisible("about_value_visible", { value: "transparency" })}
            >
              <span className="step-dot"><span>2</span></span>
              <div className="step-icon" aria-hidden="true"><ScanEye size={22} /></div>
              <h3 className="h4">Transparency</h3>
              <p className="muted">
                No mystery supply chain. You can message the grower, see the system, and trace every harvest.
              </p>
            </motion.li>

            <motion.li
              className="step"
              variants={item}
              onViewportEnter={() => onVisible("about_value_visible", { value: "local_impact" })}
            >
              <span className="step-dot"><span>3</span></span>
              <div className="step-icon" aria-hidden="true"><Route size={22} /></div>
              <h3 className="h4">Local Impact</h3>
              <p className="muted">
                Short distances keep freshness high and emissions low. Local first, always.
              </p>
            </motion.li>

            <motion.li
              className="step"
              variants={item}
              onViewportEnter={() => onVisible("about_value_visible", { value: "innovation" })}
            >
              <span className="step-dot"><span>4</span></span>
              <div className="step-icon" aria-hidden="true"><Sparkles size={22} /></div>
              <h3 className="h4">Innovation</h3>
              <p className="muted">
                From custom sensors to adaptive lighting, every improvement starts with curiosity and ends on your plate.
              </p>
            </motion.li>
          </ol>
        </motion.section>

        {/* Food safety */}
        <motion.section
          className="about-values about-trust"
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: .3 }}
          variants={{ hidden: {}, show: { transition: { staggerChildren: .06 } } }}
          onViewportEnter={() => onVisible("about_section_visible", { section: "food_safety" })}
        >
          <motion.div className="about-trust__head" variants={item}>
            <span className="spec-icon" aria-hidden="true"><BadgeCheck size={18} /></span>
            <h2 className="h3" style={{ margin: 0 }}>Registered, inspected, and rated</h2>
          </motion.div>
          <motion.p className="muted" variants={item} style={{ maxWidth: 640 }}>
            This is a small, local operation with big standards. TearDrop is registered with Essex County
            Council as a primary producer, and holds a 5-star food hygiene rating from our
            local EHO inspection.
          </motion.p>
          <motion.div className="benefit-card about-trust-card" variants={item}>
            <div className="eho-callout">
              <EhoBadge size="lg" showLabel={false} />
              <div className="eho-callout__body">
                <h3 className="h4" style={{ margin: "0 0 var(--s-1)" }}>5/5 food hygiene rating</h3>
                <p className="muted" style={{ margin: 0 }}>
                  Independently inspected and awarded the maximum rating, on top of primary producer
                  registration for the crops we grow.
                </p>
              </div>
            </div>
          </motion.div>
        </motion.section>

        {/* CTA */}
        <section
          className="about-cta"
          onMouseEnter={() => onVisible("about_section_visible", { section: "cta" })}
        >
          <div className="cta-card">
            <div>
              <h2 className="h3">Ready to try it?</h2>
              <p className="muted">Browse what’s growing now, or talk to me about a regular drop.</p>
            </div>
            <div className="cta-row">
              <Link
                to="/browse-products"
                className="btn btn-primary"
                onClick={() => track("about_cta_click", { cta: "shop_now" })}
              >
                Shop Now
              </Link>
            </div>
          </div>
        </section>

      </div>
    </Layout>
  );
}
